import { FC, useEffect, useRef } from "react"
import { Formik, FormikProps } from "formik"
import { object, string, number, boolean } from "yup"

import SidePanel from "../../side-panel"
import ManageProfileFormContent from "./form-content"
import { useToast } from "../../../hooks"
import {
  useCreatePricingProfileMutation,
  useUpdatePricingProfileMutation
} from "../../../../redux/queries"
import { PricingProfileEntity } from "../../../../types"

interface Props {
  open: boolean
  data?: PricingProfileEntity
  brandId: string
  onClose: () => void
}

const ProfileSchema = object({
  title: string().required("Title is required"),
  type: string().required("Loan type is required"),
  dp_percent: number()
    .required("Down payment is required")
    .min(0, "Must be at least 0")
    .max(100, "Must be at most 100"),
  min_credit: number()
    .required("Min credit score is required")
    .min(300, "Must be at least 300")
    .max(850, "Must be at most 850"),
  term: number().required("Term is required").min(1, "Must be at least 1"),
  sample_list_price: number()
    .required("Sample list price is required")
    .min(0, "Must be at least 0"),
  amortization_type: string().required("Amortization type is required")
})

type FormValues = {
  title: string
  type: string
  dp_percent: number | string
  min_credit: number | string
  term: number | string
  sample_list_price: number | string
  amortization_type: string
  nonqm_program: string
  primary_residence: boolean
  lock_period: number | string
  fee: number | string
  loan_purpose: string
  va_first_time_use: boolean
  first_time_homebuyer: boolean
  max_loan_amt: number | string
  preferred_investor: string
  lead_source: string
  aus: string
  arm_term: number | string
  cash_out: number | string
  dti: number | string
  label: string
}

const getInitialValues = (data?: PricingProfileEntity): FormValues => ({
  title: data?.title ?? "",
  type: data?.type ?? "",
  dp_percent: data?.dp_percent ?? "",
  min_credit: data?.min_credit ?? "",
  term: data?.term ?? "",
  sample_list_price: data?.sample_list_price ?? "",
  amortization_type: data?.amortization_type ?? "",
  nonqm_program: data?.nonqm_program ?? "",
  primary_residence: data?.primary_residence ?? true,
  lock_period: data?.lock_period ?? "30",
  fee: data?.fee ?? "0",
  loan_purpose: data?.loan_purpose ?? "",
  va_first_time_use: data?.va_first_time_use ?? false,
  first_time_homebuyer: data?.first_time_homebuyer ?? false,
  max_loan_amt: data?.max_loan_amt ?? "0",
  preferred_investor: data?.preferred_investor ?? "",
  lead_source: data?.lead_source ?? "",
  aus: data?.aus ?? "",
  arm_term: data?.arm_term ?? "0",
  cash_out: data?.cash_out ?? "0",
  dti: data?.dti ?? "0",
  label: data?.label ?? ""
})

const ManageProfile: FC<Props> = ({ open, data, brandId, onClose }) => {
  const { pushSuccessToast, pushErrorToast } = useToast()
  const formRef = useRef<FormikProps<FormValues>>(null)
  const isEdit = !!data?.id

  const [createProfile, { isSuccess: createSuccess, isError: createError }] =
    useCreatePricingProfileMutation()
  const [updateProfile, { isSuccess: updateSuccess, isError: updateError }] =
    useUpdatePricingProfileMutation()

  const setFormField = (name: string, val: string | number | boolean) => {
    formRef.current?.setFieldValue(name, val)
  }

  const onSubmit = async () => {
    const values = formRef.current?.values
    if (!values) return

    const payload = {
      ...values,
      dp_percent: Number(values.dp_percent),
      min_credit: Number(values.min_credit),
      term: Number(values.term),
      sample_list_price: Number(values.sample_list_price),
      lock_period: Number(values.lock_period),
      fee: Number(values.fee),
      max_loan_amt: Number(values.max_loan_amt),
      arm_term: Number(values.arm_term),
      cash_out: Number(values.cash_out),
      dti: Number(values.dti)
    }

    if (isEdit) {
      updateProfile({ ...payload, id: data!.id })
    } else {
      createProfile({ ...payload, brand_id: brandId })
    }

    formRef.current?.resetForm()
    onClose()
  }

  useEffect(() => {
    if (createSuccess) {
      pushSuccessToast("Profile created successfully")
    }
  }, [createSuccess, pushSuccessToast])

  useEffect(() => {
    if (updateSuccess) {
      pushSuccessToast("Profile updated successfully")
    }
  }, [updateSuccess, pushSuccessToast])

  useEffect(() => {
    if (createError) {
      pushErrorToast("Failed to create profile")
    }
  }, [createError, pushErrorToast])

  useEffect(() => {
    if (updateError) {
      pushErrorToast("Failed to update profile")
    }
  }, [updateError, pushErrorToast])

  return (
    <Formik
      initialValues={getInitialValues(data)}
      onSubmit={onSubmit}
      innerRef={formRef}
      validationSchema={ProfileSchema}
      enableReinitialize
    >
      {({ values, isValid, dirty }) => (
        <SidePanel
          title={isEdit ? "Edit Profile" : "New Profile"}
          open={open}
          onClose={onClose}
          actions={[
            {
              label: isEdit ? "Save changes" : "Create profile",
              type: "primary",
              onClick: onSubmit,
              disabled: isEdit ? !dirty : !(isValid && dirty)
            }
          ]}
        >
          <ManageProfileFormContent
            values={values}
            setFormField={setFormField}
          />
        </SidePanel>
      )}
    </Formik>
  )
}

export default ManageProfile
